import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus,
  Edit,
  Trash2,
  Play,
  Pause,
  Search,
  MoreVertical,
  Eye,
  Calendar,
  Zap,
  MessageSquare,
  CheckCircle2,
  XCircle,
  Bot,
  Phone,
  Link2,
  Unlink,
  ChevronDown,
  X,
} from "lucide-react";
import {
  getFlows,
  deleteFlow,
  toggleFlowStatus,
  allocateFlowToPhone,
  deallocateFlowFromPhone,
  getFlowsByPhoneId,
} from "../apis/flowAPI";
import {
  fetchAiPhoneSettings,
  toggleAiForPhone,
  updateAiKnowledgeBase,
  updateAiChatbotSettings,
} from "../apis/aiChatbotAPI";
import AiChatbotModal from "../components/flowList/AiChatbotModal";

const FlowList = () => {
  const navigate = useNavigate();

  // Tab state
  const [activeTab, setActiveTab] = useState("manage"); // "manage" or "allocate"

  // Existing states for Manage Flows
  const [flows, setFlows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  // AI Chatbot Modal states
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [aiPhoneOptions, setAiPhoneOptions] = useState([]);
  const [selectedPhoneId, setSelectedPhoneId] = useState("");
  const [aiToggleLoading, setAiToggleLoading] = useState(false);
  const [aiKnowledgeDraft, setAiKnowledgeDraft] = useState("");
  const [toneDraft, setToneDraft] = useState("");
  const [outOfContextDraft, setOutOfContextDraft] = useState("");
  const [languageDraft, setLanguageDraft] = useState("");
  const [roleDraft, setRoleDraft] = useState("");
  const [tasksAndRulesDraft, setTasksAndRulesDraft] = useState("");
  const [aiReplyDays, setAiReplyDays] = useState([]);
  const [aiReplyStartTime, setAiReplyStartTime] = useState("");
  const [aiReplyEndTime, setAiReplyEndTime] = useState("");
  const [aiTimezone, setAiTimezone] = useState("Asia/Kolkata");
  const [knowledgeSaving, setKnowledgeSaving] = useState(false);
  const [aiListLoading, setAiListLoading] = useState(false);

  // Allocate Flows tab states
  const [allocatePhoneOptions, setAllocatePhoneOptions] = useState([]);
  const [allocateSelectedPhoneId, setAllocateSelectedPhoneId] = useState("");
  const [allocatedFlows, setAllocatedFlows] = useState([]);
  const [allocatedLoading, setAllocatedLoading] = useState(false);
  const [allFlows, setAllFlows] = useState([]); // All flows for dropdown
  const [showAddFlowDropdown, setShowAddFlowDropdown] = useState(false);
  const [allocatingFlowId, setAllocatingFlowId] = useState(null);
  const [deallocatingFlowId, setDeallocatingFlowId] = useState(null);

  useEffect(() => {
    fetchFlows();
  }, [filter]);

  useEffect(() => {
    loadAiPhones();
  }, []);

  // Load phone options when switching to allocate tab
  useEffect(() => {
    if (activeTab === "allocate" && allocatePhoneOptions.length === 0) {
      loadAllocatePhones();
    }
  }, [activeTab]);

  // Fetch allocated flows when phone is selected in allocate tab
  useEffect(() => {
    if (activeTab === "allocate" && allocateSelectedPhoneId) {
      fetchAllocatedFlows(allocateSelectedPhoneId);
    }
  }, [allocateSelectedPhoneId, activeTab]);

  useEffect(() => {
    if (!aiModalOpen || aiPhoneOptions.length === 0) {
      return;
    }
    if (!selectedPhoneId) {
      const first = aiPhoneOptions[0];
      if (first) {
        setSelectedPhoneId(first.phone_id);
        loadPhoneData(first);
      }
    }
  }, [aiModalOpen, aiPhoneOptions, selectedPhoneId]);

  const loadPhoneData = (phone) => {
    setAiKnowledgeDraft(phone.knowledge_base || "");
    setToneDraft(phone.tone || "");
    setOutOfContextDraft(phone.out_of_context_reply || "");
    setLanguageDraft(phone.language || "");
    setRoleDraft(phone.role || "");
    setTasksAndRulesDraft(phone.tasks_and_rules || "");
    setAiReplyDays(phone.ai_reply_days || []);
    setAiReplyStartTime(phone.ai_reply_start_time || "");
    setAiReplyEndTime(phone.ai_reply_end_time || "");
    setAiTimezone(phone.ai_timezone || "Asia/Kolkata");
  };

  const filteredFlows = flows.filter(
    (flow) =>
      flow.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (flow.description &&
        flow.description.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const selectedPhone = useMemo(
    () => aiPhoneOptions.find((option) => option.phone_id === selectedPhoneId),
    [aiPhoneOptions, selectedPhoneId]
  );

  // Get unallocated flows for the dropdown
  const unallocatedFlows = useMemo(() => {
    const allocatedIds = allocatedFlows.map((f) => f.id);
    return allFlows.filter((flow) => !allocatedIds.includes(flow.id));
  }, [allFlows, allocatedFlows]);

  const fetchFlows = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("userAccessToken");
      const params = filter !== "all" ? { isActive: filter === "active" } : {};
      const response = await getFlows(token, params);
      setFlows(response.data.flows);
    } catch (error) {
      console.error("Error fetching flows:", error);
      alert("Failed to load flows");
    } finally {
      setLoading(false);
    }
  };

  const loadAiPhones = async () => {
    try {
      setAiListLoading(true);
      const response = await fetchAiPhoneSettings();
      const list = response.data?.data || [];
      setAiPhoneOptions(list);
      if (list.length > 0 && !selectedPhoneId) {
        setSelectedPhoneId(list[0].phone_id);
        loadPhoneData(list[0]);
      }
    } catch (error) {
      console.error("Error loading AI phone settings:", error);
    } finally {
      setAiListLoading(false);
    }
  };

  // Load phones for allocate tab
  const loadAllocatePhones = async () => {
    try {
      const response = await fetchAiPhoneSettings();
      const list = response.data?.data || [];
      setAllocatePhoneOptions(list);
      console.log(list);
      if (list.length > 0 && !allocateSelectedPhoneId) {
        setAllocateSelectedPhoneId(list[0].id);
      }
      // Also fetch all flows for the dropdown
      const token = localStorage.getItem("userAccessToken");
      const flowsResponse = await getFlows(token, {});
      setAllFlows(flowsResponse.data.flows || []);
    } catch (error) {
      console.error("Error loading phones for allocation:", error);
    }
  };

  // Fetch allocated flows for selected phone
  const fetchAllocatedFlows = async (phoneId) => {
    try {
      setAllocatedLoading(true);
      const token = localStorage.getItem("userAccessToken");
      const response = await getFlowsByPhoneId(token, phoneId);
      setAllocatedFlows(response.data.flows || []);
    } catch (error) {
      console.error("Error fetching allocated flows:", error);
      setAllocatedFlows([]);
    } finally {
      setAllocatedLoading(false);
    }
  };

  // Allocate flow to phone
  const handleAllocateFlow = async (flowId) => {
    try {
      setAllocatingFlowId(flowId);
      const token = localStorage.getItem("userAccessToken");
      await allocateFlowToPhone(token, flowId, allocateSelectedPhoneId);
      await fetchAllocatedFlows(allocateSelectedPhoneId);
      setShowAddFlowDropdown(false);
    } catch (error) {
      console.error("Error allocating flow:", error);
      alert("Failed to allocate flow");
    } finally {
      setAllocatingFlowId(null);
    }
  };

  // Deallocate flow from phone
  const handleDeallocateFlow = async (flowId) => {
    if (
      !window.confirm(
        "Are you sure you want to remove this flow from this phone?"
      )
    )
      return;
    try {
      setDeallocatingFlowId(flowId);
      const token = localStorage.getItem("userAccessToken");
      await deallocateFlowFromPhone(token, flowId, allocateSelectedPhoneId);
      await fetchAllocatedFlows(allocateSelectedPhoneId);
    } catch (error) {
      console.error("Error deallocating flow:", error);
      alert("Failed to remove flow");
    } finally {
      setDeallocatingFlowId(null);
    }
  };

  const handleAiToggle = async (nextValue) => {
    if (!selectedPhone) return;
    if (nextValue && !aiKnowledgeDraft.trim()) {
      const confirmed = window.confirm(
        "Warning: No knowledge base has been provided. The AI chatbot may not function properly without a knowledge base. Do you want to enable it anyway? You can add the knowledge base later."
      );
      if (!confirmed) {
        return;
      }
    }
    try {
      setAiToggleLoading(true);
      await toggleAiForPhone(selectedPhone.phone_id, nextValue);
      setAiPhoneOptions((prev) =>
        prev.map((option) =>
          option.phone_id === selectedPhone.phone_id
            ? { ...option, ai_enabled: nextValue }
            : option
        )
      );
    } catch (error) {
      console.error("Error toggling AI chatbot:", error);
      alert("Failed to update AI chatbot setting");
    } finally {
      setAiToggleLoading(false);
    }
  };

  const handleKnowledgeSave = async () => {
    if (!selectedPhone) return;
    try {
      setKnowledgeSaving(true);
      await updateAiKnowledgeBase(selectedPhone.phone_id, aiKnowledgeDraft);
      setAiPhoneOptions((prev) =>
        prev.map((option) =>
          option.phone_id === selectedPhone.phone_id
            ? { ...option, knowledge_base: aiKnowledgeDraft }
            : option
        )
      );
      alert("Knowledge base saved successfully");
    } catch (error) {
      console.error("Error saving knowledge base:", error);
      alert("Failed to save knowledge base");
    } finally {
      setKnowledgeSaving(false);
    }
  };

  const handleOpenAiModal = async () => {
    if (aiPhoneOptions.length === 0) {
      await loadAiPhones();
    }
    setAiModalOpen(true);
  };

  const handleSelectPhone = (phoneId) => {
    setSelectedPhoneId(phoneId);
    const next = aiPhoneOptions.find((option) => option.phone_id === phoneId);
    if (next) {
      loadPhoneData(next);
    }
  };

  const handleSaveAllSettings = async (updates = {}) => {
    if (!selectedPhone) return;
    try {
      setKnowledgeSaving(true);
      const settingsToSave = {
        knowledge_base:
          updates.knowledge_base !== undefined
            ? updates.knowledge_base
            : aiKnowledgeDraft,
        tone: updates.tone !== undefined ? updates.tone : toneDraft,
        out_of_context_reply:
          updates.out_of_context_reply !== undefined
            ? updates.out_of_context_reply
            : outOfContextDraft,
        language:
          updates.language !== undefined ? updates.language : languageDraft,
        role: updates.role !== undefined ? updates.role : roleDraft,
        tasks_and_rules:
          updates.tasks_and_rules !== undefined
            ? updates.tasks_and_rules
            : tasksAndRulesDraft,
        ai_reply_days:
          updates.ai_reply_days !== undefined
            ? updates.ai_reply_days
            : aiReplyDays,
        ai_reply_start_time:
          updates.ai_reply_start_time !== undefined
            ? updates.ai_reply_start_time
            : aiReplyStartTime,
        ai_reply_end_time:
          updates.ai_reply_end_time !== undefined
            ? updates.ai_reply_end_time
            : aiReplyEndTime,
        ai_timezone:
          updates.ai_timezone !== undefined ? updates.ai_timezone : aiTimezone,
      };

      await updateAiChatbotSettings(selectedPhone.phone_id, settingsToSave);

      if (updates.knowledge_base !== undefined)
        setAiKnowledgeDraft(updates.knowledge_base);
      if (updates.tone !== undefined) setToneDraft(updates.tone);
      if (updates.out_of_context_reply !== undefined)
        setOutOfContextDraft(updates.out_of_context_reply);
      if (updates.language !== undefined) setLanguageDraft(updates.language);
      if (updates.role !== undefined) setRoleDraft(updates.role);
      if (updates.tasks_and_rules !== undefined)
        setTasksAndRulesDraft(updates.tasks_and_rules);
      if (updates.ai_reply_days !== undefined)
        setAiReplyDays(updates.ai_reply_days);
      if (updates.ai_reply_start_time !== undefined)
        setAiReplyStartTime(updates.ai_reply_start_time);
      if (updates.ai_reply_end_time !== undefined)
        setAiReplyEndTime(updates.ai_reply_end_time);
      if (updates.ai_timezone !== undefined) setAiTimezone(updates.ai_timezone);

      setAiPhoneOptions((prev) =>
        prev.map((option) =>
          option.phone_id === selectedPhone.phone_id
            ? {
                ...option,
                knowledge_base: settingsToSave.knowledge_base,
                tone: settingsToSave.tone,
                out_of_context_reply: settingsToSave.out_of_context_reply,
                language: settingsToSave.language,
                role: settingsToSave.role,
                tasks_and_rules: settingsToSave.tasks_and_rules,
                ai_reply_days: settingsToSave.ai_reply_days,
                ai_reply_start_time: settingsToSave.ai_reply_start_time,
                ai_reply_end_time: settingsToSave.ai_reply_end_time,
                ai_timezone: settingsToSave.ai_timezone,
              }
            : option
        )
      );
    } catch (error) {
      console.error("Error saving settings:", error);
      alert("Failed to save settings");
      throw error;
    } finally {
      setKnowledgeSaving(false);
    }
  };

  const handleDelete = async (flowId) => {
    if (!window.confirm("Are you sure you want to delete this flow?")) return;

    try {
      const token = localStorage.getItem("userAccessToken");
      await deleteFlow(token, flowId);
      alert("Flow deleted successfully");
      fetchFlows();
    } catch (error) {
      console.error("Error deleting flow:", error);
      alert("Failed to delete flow");
    }
  };

  const handleToggleStatus = async (flowId, currentStatus) => {
    try {
      const token = localStorage.getItem("userAccessToken");
      await toggleFlowStatus(token, flowId, !currentStatus);
      alert(
        `Flow ${!currentStatus ? "activated" : "deactivated"} successfully`
      );
      fetchFlows();
    } catch (error) {
      console.error("Error toggling flow status:", error);
      alert("Failed to update flow status");
    }
  };

  const getTriggerBadge = (triggerType) => {
    const badges = {
      keyword: {
        bg: "bg-blue-100",
        text: "text-blue-700",
        label: "Keyword",
        ring: "ring-blue-200",
      },
      welcome: {
        bg: "bg-green-100",
        text: "text-green-700",
        label: "Welcome",
        ring: "ring-green-200",
      },
      menu: {
        bg: "bg-purple-100",
        text: "text-purple-700",
        label: "Menu",
        ring: "ring-purple-200",
      },
      default: {
        bg: "bg-gray-100",
        text: "text-gray-700",
        label: "Default",
        ring: "ring-gray-200",
      },
    };
    const badge = badges[triggerType] || badges.default;
    return (
      <span
        className={`px-2 py-0.5 rounded-lg text-[10px] font-semibold ${badge.bg} ${badge.text} ring-1 ${badge.ring}`}
      >
        {badge.label}
      </span>
    );
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, duration: 0.3 },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5, ease: "easeOut" },
    },
  };

  return (
    <div className="min-h-screen bg-white text-gray-900">
      <div className="mx-auto px-3 sm:px-4 lg:px-5 py-4">
        {/* Stats Cards */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div
            className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm ring-1 ring-gray-100"
            variants={itemVariants}
            whileHover={{ scale: 1.01, y: -2 }}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] font-medium text-gray-600">
                  Total Flows
                </p>
                <motion.p
                  className="text-xl font-extrabold text-gray-900"
                  initial={{ scale: 0.9 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: "spring", stiffness: 420 }}
                >
                  {flows.length}
                </motion.p>
              </div>
              <div className="p-2 rounded-lg ring-1 ring-gray-200 bg-[#01CC54]/10">
                <MessageSquare size={18} className="text-[#01CC54]" />
              </div>
            </div>
          </motion.div>

          <motion.div
            className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm ring-1 ring-gray-100"
            variants={itemVariants}
            whileHover={{ scale: 1.01, y: -2 }}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] font-medium text-gray-600">
                  Active Flows
                </p>
                <motion.p
                  className="text-xl font-extrabold text-[#01CC54]"
                  initial={{ scale: 0.9 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.25, type: "spring", stiffness: 420 }}
                >
                  {flows.filter((f) => f.is_active).length}
                </motion.p>
              </div>
              <div className="p-2 rounded-lg ring-1 ring-gray-200 bg-primary/10">
                <CheckCircle2 size={18} className="text-primary" />
              </div>
            </div>
          </motion.div>

          <motion.div
            className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm ring-1 ring-gray-100"
            variants={itemVariants}
            whileHover={{ scale: 1.01, y: -2 }}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] font-medium text-gray-600">
                  Inactive Flows
                </p>
                <motion.p
                  className="text-xl font-extrabold text-amber-600"
                  initial={{ scale: 0.9 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.3, type: "spring", stiffness: 420 }}
                >
                  {flows.filter((f) => !f.is_active).length}
                </motion.p>
              </div>
              <div className="p-2 rounded-lg ring-1 ring-gray-200 bg-amber-100">
                <XCircle size={18} className="text-amber-600" />
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Header Section */}
        <motion.div
          className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 rounded-lg border border-gray-200 bg-white p-3 shadow-sm ring-1 ring-gray-100"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.5 }}
        >
          <div className="flex items-center mb-2 sm:mb-0">
            <div className="rounded-full p-2 mr-3 ring-1 ring-gray-200 bg-[#01CC54]/10">
              <Zap size={18} className="text-[#01CC54]" />
            </div>
            <div>
              <h1 className="text-xl lg:text-2xl font-extrabold text-gray-900 mb-0.5">
                Chatbot Flows
              </h1>
              <p className="text-xs text-gray-600">
                Create and manage automated conversation flows
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            <motion.button
              onClick={handleOpenAiModal}
              className="inline-flex items-center justify-center px-3 py-1.5 text-xs rounded-lg font-semibold text-primary bg-primary/10 hover:bg-primary/20 focus:outline-none focus:ring-2 focus:ring-primary/30 shadow-sm transition"
              whileHover={{ scale: 1.02, y: -1 }}
              whileTap={{ scale: 0.97 }}
            >
              <Bot size={16} className="mr-1.5" />
              Enable AI Chatbot
            </motion.button>
            <motion.button
              onClick={() => navigate("/flow-builder")}
              className="inline-flex items-center px-3 py-1.5 text-xs rounded-lg font-semibold text-white bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary/40 shadow-sm transition"
              whileHover={{ scale: 1.03, y: -2 }}
              whileTap={{ scale: 0.97 }}
            >
              <Plus size={16} className="mr-1.5" />
              Create New Flow
            </motion.button>
          </div>
        </motion.div>

        {/* Tab Navigation */}
        <motion.div
          className="rounded-lg border border-gray-200 bg-white p-1 shadow-sm ring-1 ring-gray-100 mb-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.18, duration: 0.5 }}
        >
          <div className="flex gap-1">
            <button
              onClick={() => setActiveTab("manage")}
              className={`flex-1 sm:flex-none px-4 py-2 rounded-lg text-sm font-semibold transition-all flex items-center justify-center gap-2 ${
                activeTab === "manage"
                  ? "bg-primary text-white shadow-sm"
                  : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
              }`}
            >
              <MessageSquare size={16} />
              Manage Flows
            </button>
            <button
              onClick={() => setActiveTab("allocate")}
              className={`flex-1 sm:flex-none px-4 py-2 rounded-lg text-sm font-semibold transition-all flex items-center justify-center gap-2 ${
                activeTab === "allocate"
                  ? "bg-primary text-white shadow-sm"
                  : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
              }`}
            >
              <Link2 size={16} />
              Allocate Flows
            </button>
          </div>
        </motion.div>

        {/* Tab Content */}
        <AnimatePresence mode="wait">
          {activeTab === "manage" ? (
            <motion.div
              key="manage"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.3 }}
            >
              {/* Search and Filter Bar */}
              <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm ring-1 ring-gray-100 mb-4">
                <div className="flex flex-col md:flex-row gap-2">
                  <div className="flex-1 relative">
                    <Search
                      className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400"
                      size={16}
                    />
                    <input
                      type="text"
                      placeholder="Search flows by name or description..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/40 focus:border-primary transition bg-white"
                    />
                  </div>
                  <div className="flex gap-1.5 bg-gray-50 rounded-lg p-1 ring-1 ring-gray-200">
                    {["all", "active", "inactive"].map((tab) => (
                      <button
                        key={tab}
                        onClick={() => setFilter(tab)}
                        className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
                          filter === tab
                            ? "bg-white text-primary shadow-sm ring-1 ring-gray-200"
                            : "text-gray-600 hover:text-gray-900"
                        }`}
                      >
                        {tab.charAt(0).toUpperCase() + tab.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Flows Grid */}
              <AnimatePresence>
                {loading ? (
                  <motion.div
                    className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    {[1, 2, 3].map((i) => (
                      <div
                        key={i}
                        className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm animate-pulse"
                      >
                        <div className="h-3 bg-gray-200 rounded mb-3"></div>
                        <div className="h-6 bg-gray-200 rounded mb-1.5"></div>
                        <div className="h-3 bg-gray-200 rounded w-2/3"></div>
                      </div>
                    ))}
                  </motion.div>
                ) : filteredFlows.length === 0 ? (
                  <motion.div
                    className="text-center py-12"
                    initial={{ opacity: 0, scale: 0.96 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.4 }}
                  >
                    <div className="rounded-lg border border-gray-200 bg-white p-8 max-w-lg mx-auto shadow-sm ring-1 ring-gray-100">
                      <div className="rounded-full p-4 w-fit mx-auto mb-4 ring-1 ring-gray-200 bg-gray-50">
                        <MessageSquare size={48} className="text-gray-400" />
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2">
                        {searchQuery ? "No flows found" : "Get Started Today"}
                      </h3>
                      <p className="text-sm text-gray-600 mb-6 leading-relaxed">
                        {searchQuery
                          ? `No flows match "${searchQuery}". Try a different search term.`
                          : "Create your first chatbot flow to automate conversations and engage with your customers"}
                      </p>
                      {!searchQuery && (
                        <motion.button
                          onClick={() => navigate("/flow-builder")}
                          className="px-5 py-2.5 text-sm rounded-lg font-semibold text-white bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary/40 shadow-sm transition inline-flex items-center"
                          whileHover={{ scale: 1.03, y: -2 }}
                          whileTap={{ scale: 0.97 }}
                        >
                          <Plus size={18} className="mr-2" />
                          Create Your First Flow
                        </motion.button>
                      )}
                    </div>
                  </motion.div>
                ) : (
                  <motion.div
                    className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3"
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                  >
                    {filteredFlows.map((flow, index) => (
                      <motion.div
                        key={flow.id}
                        variants={itemVariants}
                        initial={{ opacity: 0, y: 50 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.06, duration: 0.45 }}
                      >
                        <FlowCard
                          flow={flow}
                          onEdit={() => navigate(`/flows/builder/${flow.id}`)}
                          onView={() => navigate(`/flows/builder/${flow.id}`)}
                          onDelete={() => handleDelete(flow.id)}
                          onToggleStatus={() =>
                            handleToggleStatus(flow.id, flow.is_active)
                          }
                          getTriggerBadge={getTriggerBadge}
                        />
                      </motion.div>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ) : (
            <motion.div
              key="allocate"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              <AllocateFlowsTab
                phoneOptions={allocatePhoneOptions}
                selectedPhoneId={allocateSelectedPhoneId}
                onSelectPhone={setAllocateSelectedPhoneId}
                allocatedFlows={allocatedFlows}
                loading={allocatedLoading}
                unallocatedFlows={unallocatedFlows}
                showAddDropdown={showAddFlowDropdown}
                setShowAddDropdown={setShowAddFlowDropdown}
                onAllocate={handleAllocateFlow}
                onDeallocate={handleDeallocateFlow}
                allocatingFlowId={allocatingFlowId}
                deallocatingFlowId={deallocatingFlowId}
                getTriggerBadge={getTriggerBadge}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {aiModalOpen && (
        <AiChatbotModal
          onClose={() => setAiModalOpen(false)}
          loading={aiListLoading}
          phoneOptions={aiPhoneOptions}
          selectedPhoneId={selectedPhoneId}
          onSelectPhone={handleSelectPhone}
          aiEnabled={!!selectedPhone?.ai_enabled}
          onToggleAi={handleAiToggle}
          toggleLoading={aiToggleLoading}
          knowledgeDraft={aiKnowledgeDraft}
          onKnowledgeChange={setAiKnowledgeDraft}
          toneDraft={toneDraft}
          onToneChange={setToneDraft}
          outOfContextDraft={outOfContextDraft}
          onOutOfContextChange={setOutOfContextDraft}
          languageDraft={languageDraft}
          onLanguageChange={setLanguageDraft}
          roleDraft={roleDraft}
          onRoleChange={setRoleDraft}
          tasksAndRulesDraft={tasksAndRulesDraft}
          onTasksAndRulesChange={setTasksAndRulesDraft}
          aiReplyDays={aiReplyDays}
          aiReplyStartTime={aiReplyStartTime}
          aiReplyEndTime={aiReplyEndTime}
          aiTimezone={aiTimezone}
          onScheduleChange={(data) => {
            setAiReplyDays(data.ai_reply_days);
            setAiReplyStartTime(data.ai_reply_start_time);
            setAiReplyEndTime(data.ai_reply_end_time);
            setAiTimezone(data.ai_timezone);
          }}
          onSaveAll={handleSaveAllSettings}
          saving={knowledgeSaving}
        />
      )}
    </div>
  );
};

// Allocate Flows Tab Component
const AllocateFlowsTab = ({
  phoneOptions,
  selectedPhoneId,
  onSelectPhone,
  allocatedFlows,
  loading,
  unallocatedFlows,
  showAddDropdown,
  setShowAddDropdown,
  onAllocate,
  onDeallocate,
  allocatingFlowId,
  deallocatingFlowId,
  getTriggerBadge,
}) => {
  const [searchFlow, setSearchFlow] = useState("");

  const filteredUnallocatedFlows = unallocatedFlows.filter(
    (flow) =>
      flow.name.toLowerCase().includes(searchFlow.toLowerCase()) ||
      (flow.description &&
        flow.description.toLowerCase().includes(searchFlow.toLowerCase()))
  );

  const selectedPhone = phoneOptions.find(
    (p) => p.phone_id === selectedPhoneId
  );

  return (
    <div className="space-y-4">
      {/* Phone Selector */}
      <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm ring-1 ring-gray-100">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-lg ring-1 ring-gray-200 bg-primary/10">
              <Phone size={18} className="text-primary" />
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900">
                Select WhatsApp Number
              </p>
              <p className="text-xs text-gray-500">
                Choose a phone to manage flow allocations
              </p>
            </div>
          </div>

          <div className="flex-1 sm:max-w-xs ml-auto">
            <select
              value={selectedPhoneId}
              onChange={(e) => onSelectPhone(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/40 focus:border-primary transition bg-white font-medium"
            >
              {phoneOptions.length === 0 ? (
                <option value="">No phones available</option>
              ) : (
                phoneOptions.map((phone) => (
                  <option key={phone.phone_id} value={phone.id}>
                    {phone.phone_number || phone.phone_id}
                  </option>
                ))
              )}
            </select>
          </div>
        </div>
      </div>

      {/* Allocated Flows Section */}
      <div className="rounded-lg border border-gray-200 bg-white shadow-sm ring-1 ring-gray-100">
        <div className="p-4 border-b border-gray-100">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <h3 className="text-sm font-bold text-gray-900">
                Allocated Flows
              </h3>
              <p className="text-xs text-gray-500 mt-0.5">
                {selectedPhone
                  ? `Flows assigned to ${
                      selectedPhone.phone_number || selectedPhone.phone_id
                    }`
                  : "Select a phone to view allocated flows"}
              </p>
            </div>

            {/* Add Flow Button */}
            <div className="relative">
              <motion.button
                onClick={() => setShowAddDropdown(!showAddDropdown)}
                className="inline-flex items-center px-3 py-1.5 text-xs rounded-lg font-semibold text-white bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary/40 shadow-sm transition"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                disabled={!selectedPhoneId}
              >
                <Plus size={14} className="mr-1.5" />
                Add Flow
                <ChevronDown
                  size={14}
                  className={`ml-1.5 transition-transform ${
                    showAddDropdown ? "rotate-180" : ""
                  }`}
                />
              </motion.button>

              {/* Add Flow Dropdown */}
              <AnimatePresence>
                {showAddDropdown && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setShowAddDropdown(false)}
                    />
                    <motion.div
                      className="absolute right-0 top-10 bg-white rounded-lg shadow-xl border border-gray-200 z-20 w-72 ring-1 ring-gray-100 overflow-hidden"
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.15 }}
                    >
                      {/* Search Input */}
                      <div className="p-2 border-b border-gray-100">
                        <div className="relative">
                          <Search
                            className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400"
                            size={14}
                          />
                          <input
                            type="text"
                            placeholder="Search flows..."
                            value={searchFlow}
                            onChange={(e) => setSearchFlow(e.target.value)}
                            className="w-full pl-8 pr-3 py-1.5 text-xs border border-gray-200 rounded-md focus:ring-2 focus:ring-primary/40 focus:border-primary transition bg-white"
                            autoFocus
                          />
                        </div>
                      </div>

                      {/* Flow List */}
                      <div className="max-h-48 overflow-y-auto">
                        {filteredUnallocatedFlows.length === 0 ? (
                          <div className="p-4 text-center">
                            <p className="text-xs text-gray-500">
                              {unallocatedFlows.length === 0
                                ? "All flows are already allocated"
                                : "No flows match your search"}
                            </p>
                          </div>
                        ) : (
                          filteredUnallocatedFlows.map((flow) => (
                            <button
                              key={flow.id}
                              onClick={() => onAllocate(flow.id)}
                              disabled={allocatingFlowId === flow.id}
                              className="w-full px-3 py-2.5 text-left hover:bg-gray-50 transition-colors flex items-center justify-between gap-2 border-b border-gray-50 last:border-b-0"
                            >
                              <div className="min-w-0 flex-1">
                                <p className="text-xs font-semibold text-gray-900 truncate">
                                  {flow.name}
                                </p>
                                <p className="text-[10px] text-gray-500 truncate">
                                  {flow.description || "No description"}
                                </p>
                              </div>
                              {allocatingFlowId === flow.id ? (
                                <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                              ) : (
                                <Link2
                                  size={14}
                                  className="text-gray-400 flex-shrink-0"
                                />
                              )}
                            </button>
                          ))
                        )}
                      </div>
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>

        {/* Allocated Flows List */}
        <div className="p-4">
          {loading ? (
            <div className="space-y-3">
              {[1, 2].map((i) => (
                <div
                  key={i}
                  className="rounded-lg border border-gray-200 p-3 animate-pulse"
                >
                  <div className="h-4 bg-gray-200 rounded w-1/3 mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded w-2/3"></div>
                </div>
              ))}
            </div>
          ) : allocatedFlows.length === 0 ? (
            <div className="text-center py-8">
              <div className="rounded-full p-3 w-fit mx-auto mb-3 ring-1 ring-gray-200 bg-gray-50">
                <Unlink size={24} className="text-gray-400" />
              </div>
              <p className="text-sm font-medium text-gray-900 mb-1">
                No flows allocated
              </p>
              <p className="text-xs text-gray-500">
                Click "Add Flow" to allocate flows to this phone
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {allocatedFlows.map((flow) => (
                <motion.div
                  key={flow.id}
                  className="rounded-lg border border-gray-200 p-3 bg-white hover:shadow-sm transition-shadow ring-1 ring-gray-100"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  layout
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="text-sm font-semibold text-gray-900 truncate">
                          {flow.name}
                        </h4>
                        <span
                          className={`flex-shrink-0 w-2 h-2 rounded-full ${
                            flow.is_active ? "bg-[#01CC54]" : "bg-gray-400"
                          }`}
                        />
                      </div>
                      <p className="text-xs text-gray-500 line-clamp-1 mb-2">
                        {flow.description || "No description"}
                      </p>
                      <div className="flex items-center gap-2 flex-wrap">
                        {getTriggerBadge(flow.trigger_type)}
                        <span
                          className={`text-[10px] font-semibold px-2 py-0.5 rounded-lg ring-1 ${
                            flow.is_active
                              ? "bg-green-50 text-green-700 ring-green-200"
                              : "bg-gray-100 text-gray-600 ring-gray-200"
                          }`}
                        >
                          {flow.is_active ? "Active" : "Inactive"}
                        </span>
                      </div>
                    </div>

                    <motion.button
                      onClick={() => onDeallocate(flow.id)}
                      disabled={deallocatingFlowId === flow.id}
                      className="flex-shrink-0 p-2 rounded-lg text-red-500 hover:bg-red-50 hover:text-red-600 transition-colors ring-1 ring-transparent hover:ring-red-200"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      title="Remove flow"
                    >
                      {deallocatingFlowId === flow.id ? (
                        <div className="w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <X size={16} />
                      )}
                    </motion.button>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Flow Card Component (unchanged from original)
const FlowCard = ({
  flow,
  onEdit,
  onView,
  onDelete,
  onToggleStatus,
  getTriggerBadge,
}) => {
  const [showMenu, setShowMenu] = useState(false);

  return (
    <motion.div
      className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden group cursor-pointer ring-1 ring-gray-100"
      onClick={onView}
      whileHover={{
        scale: 1.01,
        y: -2,
        boxShadow:
          "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
      }}
      transition={{ duration: 0.2 }}
    >
      <div
        className={`h-0.5 ${flow.is_active ? "bg-[#01CC54]" : "bg-gray-300"}`}
      ></div>

      <div className="p-3">
        <div className="flex justify-between items-start mb-2.5">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 mb-1.5">
              <h3 className="text-base font-bold text-gray-900 truncate">
                {flow.name}
              </h3>
              <motion.span
                className={`flex-shrink-0 w-2 h-2 rounded-full ${
                  flow.is_active ? "bg-[#01CC54]" : "bg-gray-400"
                }`}
                animate={flow.is_active ? { scale: [1, 1.2, 1] } : {}}
                transition={{ duration: 2, repeat: Infinity }}
              ></motion.span>
            </div>
            <p className="text-xs text-gray-600 line-clamp-2 min-h-[2rem] leading-relaxed">
              {flow.description || "No description provided"}
            </p>
          </div>

          <div className="relative ml-1.5">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowMenu(!showMenu);
              }}
              className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors ring-1 ring-transparent hover:ring-gray-200"
            >
              <MoreVertical size={16} className="text-gray-500" />
            </button>

            {showMenu && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowMenu(false);
                  }}
                ></div>
                <motion.div
                  className="absolute right-0 top-8 bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-20 min-w-[150px] ring-1 ring-gray-100"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.15 }}
                >
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onView();
                      setShowMenu(false);
                    }}
                    className="w-full px-3 py-2 text-left text-xs font-medium text-gray-700 hover:bg-gray-50 flex items-center gap-2 transition-colors"
                  >
                    <Eye size={14} />
                    View Flow
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit();
                      setShowMenu(false);
                    }}
                    className="w-full px-3 py-2 text-left text-xs font-medium text-gray-700 hover:bg-gray-50 flex items-center gap-2 transition-colors"
                  >
                    <Edit size={14} />
                    Edit Flow
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleStatus();
                      setShowMenu(false);
                    }}
                    className="w-full px-3 py-2 text-left text-xs font-medium text-gray-700 hover:bg-gray-50 flex items-center gap-2 transition-colors"
                  >
                    {flow.is_active ? <Pause size={14} /> : <Play size={14} />}
                    {flow.is_active ? "Deactivate" : "Activate"}
                  </button>
                  <div className="border-t border-gray-100 my-1"></div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete();
                      setShowMenu(false);
                    }}
                    className="w-full px-3 py-2 text-left text-xs font-medium text-red-600 hover:bg-red-50 flex items-center gap-2 transition-colors"
                  >
                    <Trash2 size={14} />
                    Delete Flow
                  </button>
                </motion.div>
              </>
            )}
          </div>
        </div>

        <div className="mb-2.5">
          <div className="flex items-center gap-1.5 mb-1.5 flex-wrap">
            {getTriggerBadge(flow.trigger_type)}
            <span
              className={`text-[10px] font-semibold px-2 py-0.5 rounded-lg ring-1 ${
                flow.is_active
                  ? "bg-green-50 text-green-700 ring-green-200"
                  : "bg-gray-100 text-gray-600 ring-gray-200"
              }`}
            >
              {flow.is_active ? "Active" : "Inactive"}
            </span>
          </div>
          {flow.trigger_type === "keyword" &&
            flow.trigger_keywords?.length > 0 && (
              <div className="mt-1.5 flex flex-wrap gap-1">
                {flow.trigger_keywords.slice(0, 3).map((keyword, idx) => (
                  <span
                    key={idx}
                    className="text-[10px] bg-blue-50 text-blue-700 px-2 py-0.5 rounded-lg font-medium ring-1 ring-blue-200"
                  >
                    {keyword}
                  </span>
                ))}
                {flow.trigger_keywords.length > 3 && (
                  <span className="text-[10px] text-gray-500 px-2 py-0.5 font-medium">
                    +{flow.trigger_keywords.length - 3} more
                  </span>
                )}
              </div>
            )}
        </div>

        <div className="flex items-center gap-3 text-[10px] text-gray-500 mb-2.5 pb-2.5 border-t border-gray-100 pt-2.5">
          <div className="flex items-center gap-1">
            <Calendar size={12} />
            <span className="font-medium">
              {new Date(flow.created_at).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
              })}
            </span>
          </div>
        </div>

        <div className="flex gap-1.5">
          <motion.button
            onClick={(e) => {
              e.stopPropagation();
              onView();
            }}
            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-primary/10 text-primary hover:bg-primary/20 rounded-lg transition-colors font-semibold text-xs ring-1 ring-primary/20"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Eye size={14} />
            View
          </motion.button>
          <motion.button
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
            }}
            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-gray-50 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors font-semibold text-xs ring-1 ring-gray-200"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Edit size={14} />
            Edit
          </motion.button>
        </div>
      </div>
    </motion.div>
  );
};

export default FlowList;
